import { PAYMENT_TYPES } from '@vend/business-components/dist/constants/payment-types'
import { round, subtract, vn } from 'vend-number'

import { useCallback } from 'react'
import {
  hasValidCustomerWithLoyalty,
  isOnAccountLimitDisabled,
  isOnAccountLimitUnlimited,
} from '../../../../functions/customer'
import {
  useRetailerSettingsManager,
  useUserManager,
} from '../../../../ng-compat/hooks'
import { useAppSelector } from '../../../../state/hooks'
import { selectCurrentSaleAsSale } from '../../../../state/slices/currentSale/currentSaleSlice'

import { hasKlarnaPayments } from '../../../../functions/payment-types/klarna'
import { shouldDisableOnAccountPayment } from '../../../../functions/payment-types/onaccount'
import { isABPAddedButNotPaid } from '../../../../functions/payments'
import {
  getOutstandingBalance,
  isAccountBalancePaymentAdded,
  isLegacyServiceSale,
  isMarkedAsUnfulfilled,
  isOnAccountLimitReached,
  isPartialPaidSale,
  isReturn,
  isValidCustomer,
} from '../../../../functions/sale'

import { customerBalancesManager } from '../../../../managers/customer-balances-manager'
import { SaleStatus } from '../../../../models/sale.constants'
import { PERMISSIONS } from '../../../../constants/permissions'

import { getFeatureFromCache } from '../../../../util/feature-cache'
import { useIsOffline } from '../../../shared/useIsOffline'
import { useAvailableStoreCredit } from './useAvailableStoreCredit'
import { useCustomerLoyaltyInfo } from './useCustomerLoyaltyInfo'

/**
 * Hook to determine disabled state of customer payment options: Loyalty, Store Credit, layby, and onAccount
 *
 */
export const usePaymentDisabledState = (
  amountTendered: string,
  hasPaymentAmountError: boolean
) => {
  const retailerSettingsManager = useRetailerSettingsManager()
  const userManager = useUserManager()
  const currentSale = useAppSelector(selectCurrentSaleAsSale)
  const availableStoreCredit = useAvailableStoreCredit()
  const customerLoyaltyEnabled = hasValidCustomerWithLoyalty(
    currentSale.customer || undefined
  )
  const { isOffline } = useIsOffline()
  const { thresholdEnabled, hasEnoughLoyalty } = useCustomerLoyaltyInfo()

  /**
   * Update the disabled state of the loyalty button.
   * Loyalty is disabled if
   * - there is no customer
   * - the customer has loyalty disabled (or a loyalty balance of 0)
   * - the amount tendered is $0 or less
   * - the client is offline.
   * - the account balance payment is added but not paid (can't pay ABP by loyalty)
   * - the customer loyalty balance is less than the loyalty threshold value
   */
  const isLoyaltyPaymentDisabled = useCallback(() => {
    if (isOffline) {
      return true
    }
    if (hasPaymentAmountError) {
      return true
    }
    if (isNaN(Number(amountTendered))) {
      return true
    }

    if (customerLoyaltyEnabled) {
      if (thresholdEnabled && !hasEnoughLoyalty) {
        return true
      }
      const totalLoyaltyPartialPayments = customerBalancesManager.getTotalPartialPayments(
        PAYMENT_TYPES.LOYALTY,
        currentSale
      )
      const loyaltyBalance = currentSale.customer!.loyalty_balance
      const availableLoyaltyBalance = round(
        subtract(loyaltyBalance, totalLoyaltyPartialPayments),
        2
      )

      if (
        vn(availableLoyaltyBalance).lte(0) ||
        vn(amountTendered).lte(0) ||
        isABPAddedButNotPaid(currentSale)
      ) {
        return true
      }

      return false
    } else {
      return true
    }
  }, [
    isOffline,
    customerLoyaltyEnabled,
    amountTendered,
    hasPaymentAmountError,
    currentSale,
    hasEnoughLoyalty,
    thresholdEnabled,
  ])

  /**
   * Update the disabled state of the store credit button. Store credit is
   * disabled if
   * - there is no customer
   * - when a positive balance is present but store credit is unavailable
   * - account balance payment is added to sale and store credit is less than the amount
   */
  const isStoreCreditPaymentDisabled = useCallback(() => {
    if (hasPaymentAmountError) {
      return true
    }
    const balance = getOutstandingBalance(currentSale)
    const hasNegativeBalance = vn(balance).isNeg()
    const hasValidCustomer = isValidCustomer(currentSale.customer)

    if (hasValidCustomer) {
      // The store credit button is enabled for both negative and positive balances. If we use it with a negative
      // balance the customer will be credited. If you want to spend store credit you have to be on a positive balance
      // sale and have store credit available.
      if (vn(availableStoreCredit).lte(0) && !hasNegativeBalance) {
        return true
      }
      if (
        !hasNegativeBalance &&
        !(
          currentSale.customerBalances &&
          currentSale.customerBalances.storeCredit
        )
      ) {
        return true
      }

      // Disable store credit if Klarna payments have been made on the sale.
      if (
        hasKlarnaPayments({
          payments: currentSale.originalSalePayments,
        })
      ) {
        return true
      }

      if (
        isAccountBalancePaymentAdded(currentSale.payAccountBalance) &&
        vn(currentSale.payAccountBalance!.amount).gt(availableStoreCredit)
      ) {
        return true
      }
      return false
    }
    return true
  }, [currentSale, hasPaymentAmountError, availableStoreCredit])

  const isPartialPayment = useCallback(() => {
    const outstandingBalance = getOutstandingBalance(currentSale)
    const getRemainingAmountIfTendered = () => {
      if (!amountTendered || isNaN(Number(amountTendered))) {
        return 0 // return 0 to disable the payment button
      }
      if (!isFinite(outstandingBalance) || !isFinite(Number(amountTendered))) {
        return 0
      }
      return subtract(outstandingBalance, amountTendered)
    }

    return getRemainingAmountIfTendered() > 0 && outstandingBalance > 0
  }, [amountTendered, currentSale])

  /**
   * Disabled check for the LEGACY On Account sale-status flow
   * (on_account_payment_type FF is OFF).
   * Transitions the sale to ONACCOUNT status rather than adding a payment.
   * Do NOT use this for the payment-type flow —
   * see isOnAccountPaymentTypeDisabled below.
   */
  const isOnAccountPaymentDisabled = useCallback(() => {
    if (hasPaymentAmountError) {
      return true
    }
    if (!isValidCustomer(currentSale.customer)) {
      return true
    }
    const balance = getOutstandingBalance(currentSale)
    const hasNegativeBalance = vn(balance).isNeg()

    const notReturnSaleAndPositiveBalance =
      !isReturn(currentSale) && !hasNegativeBalance

    const hasXeroEnabled = retailerSettingsManager.isXeroIntegrationEnabled()
    if (notReturnSaleAndPositiveBalance || hasXeroEnabled) {
      if (
        currentSale.displayStatus !== SaleStatus.LAYBY_SALE &&
        userManager.hasPermission(PERMISSIONS.SALE_ON_ACCOUNT)
      ) {
        const shouldEnforceOnAccountLimit = getFeatureFromCache(
          'enable_on_account_limit_enforcement'
        )
        const isSavedUnfulfilledWithPayments =
          currentSale.isSavedUnfulfilled && isPartialPaidSale(currentSale)
        const isRestrictedByOnAccountLimit =
          shouldEnforceOnAccountLimit &&
          (isOnAccountLimitDisabled(currentSale.customer) ||
            (!isOnAccountLimitUnlimited(currentSale.customer) &&
              isOnAccountLimitReached(currentSale)))
        const shouldDisabled =
          isSavedUnfulfilledWithPayments ||
          isLegacyServiceSale(currentSale.status) ||
          isRestrictedByOnAccountLimit

        if (shouldDisabled) {
          return true
        }
      } else {
        return true
      }
    }
    if (isABPAddedButNotPaid(currentSale)) {
      return true
    }
    if (isPartialPayment()) {
      return true
    }
    return false
  }, [
    currentSale,
    hasPaymentAmountError,
    isPartialPayment,
    retailerSettingsManager,
    userManager,
  ])

  /**
   * Disabled check for the On Account PAYMENT TYPE flow
   * (on_account_payment_type FF is ON).
   * Adds a real payment that debits the customer's account balance.
   * Do NOT conflate with isOnAccountPaymentDisabled, which handles the legacy
   * sale-status transition flow.
   *
   */
  const isOnAccountPaymentTypeDisabled = useCallback(() => {
    if (hasPaymentAmountError) {
      return true
    }
    const isSavedUnfulfilledWithPayments =
      currentSale.isSavedUnfulfilled && isPartialPaidSale(currentSale)
    const shouldBeDisabled =
      isSavedUnfulfilledWithPayments || isLegacyServiceSale(currentSale.status)

    if (shouldBeDisabled) {
      return true
    }

    if (
      shouldDisableOnAccountPayment({
        customer: currentSale.customer,
        amountTendered,
        sale: currentSale,
      })
    ) {
      return true
    }

    if (isABPAddedButNotPaid(currentSale)) {
      return true
    }

    // Partial and mixed payments are allowed for the payment-type flow —
    // see shouldDisableOnAccountPayment.
    return false
  }, [currentSale, amountTendered, hasPaymentAmountError])

  const isLaybyPaymentDisabled = useCallback(() => {
    if (hasPaymentAmountError) {
      return true
    }
    const shouldDisabled =
      !isValidCustomer(currentSale.customer) ||
      isMarkedAsUnfulfilled(currentSale.status) ||
      isLegacyServiceSale(currentSale.status)

    if (shouldDisabled) {
      return true
    }
    if (isABPAddedButNotPaid(currentSale)) {
      return true
    }
    if (isPartialPayment()) {
      return true
    }
    return false
  }, [currentSale, hasPaymentAmountError, isPartialPayment])

  const isPaymentMethodOnFileDisabled = useCallback(() => {
    if (hasPaymentAmountError) {
      return true
    }

    return vn(amountTendered).lte(0)
  }, [hasPaymentAmountError, amountTendered])

  const getDisabledState = useCallback(
    (paymentId: string) => {
      switch (paymentId) {
        case 'loyalty': {
          return isLoyaltyPaymentDisabled()
        }
        case 'storeCredit': {
          return isStoreCreditPaymentDisabled()
        }
        case 'LAYBY': {
          return isLaybyPaymentDisabled()
        }
        case 'ONACCOUNT': {
          return isOnAccountPaymentDisabled()
        }
        case 'onAccountPaymentType': {
          return isOnAccountPaymentTypeDisabled()
        }
        case 'pmof': {
          return isPaymentMethodOnFileDisabled()
        }
        default:
          return true
      }
    },
    [
      isLoyaltyPaymentDisabled,
      isStoreCreditPaymentDisabled,
      isLaybyPaymentDisabled,
      isOnAccountPaymentDisabled,
      isOnAccountPaymentTypeDisabled,
      isPaymentMethodOnFileDisabled,
    ]
  )

  // hook return
  return {
    getDisabledState,
  }
}
